import React, { useContext, useMemo, useState } from 'react';

import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

// Helper.
import Helper from '../../../helpers/Helper';

// Types.
import HostTarget from '../../../types/host/HostTarget';
import ChartSeries from '../../../types/ChartSeries';
import ChartPlotLine from '../../../types/ChartPlotLine';
import HostStatisticV2 from '../../../types/host/HostStatisticV2';

// Constants.
import { DATA_INITIALISING, DATA_LOADING, DATA_LOADED, CHART_DATA_TYPE } from '../../Constants';
import { CHART_COLOURS_STATEMENTS } from '../../Constants';
import { VIEW_MODE } from '../../Constants';
import { getConvertedTimeToUTC, highchartsCredits } from "../../../helpers/utils";
import { useLicences } from "../../../context/LicenceContext";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";
import ConditionalRender from "../../../helpers/ConditionalRender";
import HostApi from "../../../api/host/HostApi";

function MemoryChart(props: {host: HostTarget, plotLines?: ChartPlotLine[] }) {
    const [loadingBasic, setLoadingBasic] = useState<number>(DATA_LOADING);
    const [loadingDetail, setLoadingDetail] = useState<number>(DATA_INITIALISING);

    const [series, setSeries] = useState<ChartSeries[]>([]);
    const [timeSeries, setTimeSeries] = useState<number[][]>([]);
    // Hold onto the v2 response so toggling to the breakdown view does not need a second call.
    const [v2Response, setV2Response] = useState<HostStatisticV2[]>([]);
    const [mode, setMode] = useState<number>(VIEW_MODE.BASIC);

    const timePeak = useMemo(() => {
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])

    const timeRangeContext = useContext(TimeRangeContext)
    const {licences} = useLicences();

    void useMemo(async() => {
        const limit = process.env.REACT_APP_API_LIMIT ? Number(process.env.REACT_APP_API_LIMIT) : undefined;
        const data = await HostApi.getHostStatisticV2Time(buildApiQuery(timeRangeContext.timeRange), [props.host.id], limit);
        setV2Response(data);
        setTimeSeries(data.map(item => {
            return [getConvertedTimeToUTC(item), Number(item.memoryutilisation) || 0]
        }));
        setLoadingBasic(DATA_LOADED);
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.host]);

    function buildDetail(data: HostStatisticV2[]) {
        const metrics: string[] = ['K used memory', 'K free memory', 'K buffer memory', 'K swap cache'];
        const seriesData: any[] = metrics.map((metric, index) => ({
            name: metric,
            color: CHART_COLOURS_STATEMENTS[index],
            type: 'column',
            data: data.map(item => [
                getConvertedTimeToUTC(item),
                Number(item[metric]) || 0,
            ]),
            zIndex: 1,
        }));
        setSeries(seriesData);
        setLoadingDetail(DATA_LOADED);
    }

    const basicChart = useMemo(() => {
        const basicChartOptions = chartOptionsGranularity([
            {
                name: 'Average Memory',
                type: 'spline',
                minHeight: 3,
                data: timeSeries,
                zIndex: 1,
                yAxis: 1,
            }], timePeak, {
            credits: {enabled: highchartsCredits(licences)},
            timeRangeContext: timeRangeContext,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.PERCENTAGE);
                }
            },
            plotLines: props.plotLines || []
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={basicChartOptions}/>;

    }, [timeSeries, timePeak, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, licences, props.plotLines]);

    const detailChart = useMemo(() => {
        const detailChartOptions = chartOptionsGranularity(
            series, 100, {
                credits: {enabled: highchartsCredits(licences)},
                timeRangeContext: timeRangeContext,
                legend: {
                    align: 'left',
                    backgroundColor: 'transparent',
                    enabled: false,
                    floating: false,
                    itemMarginTop: 0,
                    itemMarginBottom: 2,
                    itemStyle: {
                        fontSize: '.7rem'
                    },
                    layout: 'horizontal',
                    padding: 0,
                    x: 0,
                    y: 0,
                    verticalAlign: 'top'
                },
                tooltip: {
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.GENERIC);
                    }
                },
                plotLines: props.plotLines || []
            })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={detailChartOptions}/>;

    }, [series, timePeak, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, licences, props.plotLines]);

    function toggleChart() {

        if (loadingDetail === DATA_INITIALISING) {
            setLoadingDetail(DATA_LOADING)
            buildDetail(v2Response);
        }

        // Toggle the displayed chart.
        setMode(((mode === VIEW_MODE.BASIC) ? VIEW_MODE.DETAIL : VIEW_MODE.BASIC));
    }

    return (
        <div className="card collapsible">
            <div className="card-header">
                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                Average memory over time
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapseMemoryCharts" aria-expanded="false" aria-controls="collapseMemoryCharts"/>
                <div className="btn-group float-end">
                    <button type="button" className="btn btn-xsm btn-primary float-end ms-1"
                            onClick={toggleChart}>{((mode === VIEW_MODE.BASIC) ? 'Show Breakdown' : 'Show Summary')}</button>
                </div>
            </div>
            <div id="collapseMemoryCharts" className="card-body collapse show p-0">
                <div className="row row-cols-1">
                    <div className="col p-3">
                        <React.Fragment>

                            <h4 className='icons-chart-title'>
                                Average Memory
                                {timePeak !== null && (
                                    <span className="peak">Peak Average Memory {Math.round(timePeak)}%</span>
                                )}
                            </h4>


                            <ConditionalRender if={mode === VIEW_MODE.BASIC}>
                                <ConditionalRender if={loadingBasic === DATA_LOADING}>
                                    <div className="w-100 text-center text-muted mt-3">
                                        <div className="loader spinner chartSpinner">
                                        </div>
                                        <p className="mt-3">
                                            Loading data...
                                        </p>
                                    </div>
                                </ConditionalRender>
                                <ConditionalRender if={loadingBasic === DATA_LOADED}>
                                    {basicChart}
                                </ConditionalRender>
                            </ConditionalRender>

                            <ConditionalRender if={mode === VIEW_MODE.DETAIL}>
                                <ConditionalRender if={loadingDetail === DATA_LOADING}>
                                    <div className="w-100 text-center text-muted mt-3">
                                        <div className="loader spinner chartSpinner">
                                        </div>
                                        <p className="mt-3">
                                            Loading data...
                                        </p>
                                    </div>
                                </ConditionalRender>
                                <ConditionalRender if={loadingDetail === DATA_LOADED}>
                                    {detailChart}
                                </ConditionalRender>
                            </ConditionalRender>

                        </React.Fragment>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MemoryChart;
